import { useMemo } from 'react';

import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useOptionTranslator } from '@/components/datamodel/hooks/useOptionTranslator';
import { useDataModel } from '@/hooks/useDataModel';
import { useTranslation } from '@/hooks/useTranslation';
import { type DataModel, type DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';

const ASSET_FILTER_LAYOUT = 'asset_filter';
const COMPLIANT = 'compliant__';

interface AssetFilterOption {
    key: string;
    label: string;
}

interface AssetFilterGroup {
    attributeName: string;
    label: string;
    options: AssetFilterOption[];
}

interface FilterGroupTranslators {
    translateAttributeLabel: (attribute: DataModelAttribute) => string;
    translateOption: (option: string, attribute: DataModelAttribute) => string;
    translateBoolean?: (value: boolean, attribute: DataModelAttribute) => string;
}

type OptionGroupDataModel = Pick<DataModel, 'assetLayout' | 'attribute' | 'optionList'>;

/**
 * `Enum(:list)` attributes name their option list in `params.values`, older ones in `params.keys`;
 * inline enums carry the options themselves in `params.values`.
 */
const resolveOptions = (dataModel: OptionGroupDataModel | undefined, attribute: DataModelAttribute) => {
    if (attribute.typeName === DataModelAttributeTypeName.Boolean) {
        return [{ key: 'true' }, { key: 'false' }];
    }
    const { keys, values } = attribute.params ?? {};
    const optionListName = [keys, values].find((candidate): candidate is string => typeof candidate === 'string');
    if (optionListName) {
        return dataModel?.optionList(optionListName)?.options ?? [];
    }
    return Array.isArray(values) ? values : [];
};

/**
 * One group per attribute of the given asset layout, in layout order. Only attributes
 * backed by an already loaded option list become groups; service-backed lists
 * that are not hydrated yet are skipped rather than rendered empty. Boolean attributes
 * get the options `true` and `false`, labelled by `translateBoolean`.
 */
const buildAssetOptionGroups = (
    dataModel: OptionGroupDataModel | undefined,
    layoutName: string,
    { translateAttributeLabel, translateOption, translateBoolean }: FilterGroupTranslators,
): AssetFilterGroup[] => {
    const layout = dataModel?.assetLayout(layoutName);
    if (!layout) {
        return [];
    }
    return layout.sections
        .flatMap((section) => section.attributes)
        .filter((stub) => !stub.hidden)
        .flatMap((stub) => {
            const registered = dataModel?.attribute(stub.name);
            const attribute: DataModelAttribute = registered ? { ...registered, ...stub } : stub;
            const options = resolveOptions(dataModel, attribute);
            if (options.length === 0) {
                return [];
            }
            return [{
                attributeName: attribute.name,
                label: translateAttributeLabel(attribute),
                options: options
                    .filter((option) => !('disabled' in option && option.disabled))
                    .map((option) => ({
                        key: option.key,
                        label: attribute.typeName === DataModelAttributeTypeName.Boolean && translateBoolean
                            ? translateBoolean(option.key === 'true', attribute)
                            : translateOption(option.key, attribute),
                    })),
            }];
        });
};

const buildAssetFilterGroups = (dataModel: OptionGroupDataModel | undefined, translators: FilterGroupTranslators) =>
    buildAssetOptionGroups(dataModel, ASSET_FILTER_LAYOUT, translators);

const useAssetOptionGroups = (layoutName: string) => {
    const { dataModel } = useDataModel();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { translateOption } = useOptionTranslator();
    const { t } = useTranslation();

    return useMemo(() => {
        const translateBoolean = (value: boolean, attribute: DataModelAttribute) => (attribute.name === COMPLIANT
            ? t(value ? 'DAM.FILTER.COMPLIANT' : 'DAM.CARD.NON_COMPLIANT')
            : t(value ? 'YES' : 'NO'));
        const translateGroupLabel = (attribute: DataModelAttribute) =>
            (attribute.name === COMPLIANT ? t('DAM.FIELD.COMPLIANCE') : translateAttributeLabel(attribute));
        return buildAssetOptionGroups(dataModel, layoutName, {
            translateAttributeLabel: translateGroupLabel,
            translateOption,
            translateBoolean,
        });
    }, [dataModel, layoutName, translateAttributeLabel, translateOption, t]);
};

const useAssetFilterGroups = () => useAssetOptionGroups(ASSET_FILTER_LAYOUT);

export { ASSET_FILTER_LAYOUT, buildAssetFilterGroups, useAssetFilterGroups, useAssetOptionGroups };
export type { AssetFilterGroup, AssetFilterOption };
